import { Component } from "@theme/component";
import { DialogComponent } from '@theme/dialog';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class CartShareComponent extends Component {
  #hashPrefix = '#shared-cart/';
  #urlLimit = 1800;
  #contextParams = ['preview_theme_id', '_fd', 'pb'];
  #sectionId = 'section-rendering-cart-share';
  #sectionSelector = '.shopify-section-overlay-cart-share';
  #dialogSelector = '#cart-share-dialog';
  #source = 'cart-share-component';

  #cachedContent = null;
  #activeFetch = null;
  #restorePromise = null;
  #restoreState = 'idle';
  #restoringOverlay = null;
  #restoringContainer = null;
  #overlayTemplateSelector = '[data-shared-cart-restoring-template]';
  #cartItemsSelector = 'cart-items-component';
  #restoringAttribute = 'data-shared-cart-restoring';

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleOpen);
    this.addEventListener('keydown', this.#handleKeydown);
    window.addEventListener('hashchange', this.#handleHashChange);
    this.#restoreSharedCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleOpen);
    this.removeEventListener('keydown', this.#handleKeydown);
    window.removeEventListener('hashchange', this.#handleHashChange);
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#removeRestoringOverlay();
  }

  #getCartItemsContainer() {
    const drawerContainer = this.#getCartDrawer()?.querySelector(this.#cartItemsSelector);
    const pageContainer = this.closest('.shopify-section')?.querySelector(this.#cartItemsSelector);
    const container = drawerContainer || pageContainer || document.querySelector(this.#cartItemsSelector);

    return container;
  }

  async #prepareDrawerRestoreState() {
    await this.#openCartDrawer();
    this.#ensureRestoringOverlay();
    this.#setRestoringState(true);
  }

  #getCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    return cartDrawer instanceof DialogComponent ? cartDrawer : null;
  }

  #hasCartDrawer() {
    return Boolean(this.#getCartDrawer());
  }

  async #openCartDrawer() {
    const cartDrawer = this.#getCartDrawer();
    if (!cartDrawer || typeof cartDrawer.openDialog !== 'function') return;
    await cartDrawer.openDialog();
  }

  #shouldUseCurrentPageUrl() {
    return this.closest('cart-drawer-component') instanceof HTMLElement;
  }

  #hasSharedCartPayload(url = new URL(window.location.href)) {
    return url.hash.startsWith(this.#hashPrefix);
  }

  #removeRestoringOverlay() {
    this.#restoringContainer?.removeAttribute(this.#restoringAttribute);
    this.#restoringContainer?.querySelector('.shared-cart-restoring')?.remove();
    this.#restoringOverlay = null;
    this.#restoringContainer = null;
  }

  #ensureRestoringOverlay() {
    if (this.#restoringOverlay?.isConnected) return;

    const template = this.querySelector(this.#overlayTemplateSelector);
    const overlay = template?.firstElementChild;
    const container = this.#getCartItemsContainer();

    if (!(overlay instanceof HTMLElement) || !(container instanceof HTMLElement)) return;

    const existingOverlay = Array.from(container.children).find((child) => child.classList?.contains('shared-cart-restoring'));
    if (existingOverlay instanceof HTMLElement) {
      this.#restoringContainer = container;
      this.#restoringOverlay = existingOverlay;
      return;
    }

    this.#restoringContainer = container;
    this.#restoringOverlay = overlay.cloneNode(true);
    this.#restoringOverlay.classList.remove('hidden');
    container.append(this.#restoringOverlay);
  }

  #handleKeydown = async (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    await this.#openDialog();
  };

  #handleOpen = async (event) => {
    event.preventDefault();
    await this.#openDialog();
  };

  #handleHashChange = async () => {
    if (!this.#hasSharedCartPayload()) return;

    this.#restorePromise = null;
    this.#restoreState = 'idle';
    await this.#restoreSharedCart();
  };

  async #openDialog() {
    this.classList.add('btn--loading');

    try {
      const dialog = await this.#ensureDialog();
      if (!dialog) return;

      await this.#prepareShareUrl(dialog);
      dialog.openDialog();
    } finally {
      this.classList.remove('btn--loading');
    }
  }

  async #ensureDialog() {
    let sectionElement = this.#getDialogSectionElement();

    if (!this.#cachedContent) {
      const html = await this.#fetchDialogView();
      const fetchedSection = html?.querySelector(this.#sectionSelector);
      if (!fetchedSection) return null;
      this.#cachedContent = fetchedSection.cloneNode(true);
    }

    if (!sectionElement) {
      sectionElement = document.createElement('div');
      sectionElement.className = 'shopify-section shopify-section-overlay-cart-share';
      document.body.appendChild(sectionElement);
    }

    sectionElement.innerHTML = this.#cachedContent.innerHTML;

    const dialog = sectionElement.querySelector(this.#dialogSelector);
    return dialog instanceof DialogComponent ? dialog : null;
  }

  #getDialogSectionElement(root = document) {
    return root.querySelector(this.#sectionSelector);
  }

  async #fetchDialogView() {
    const sectionUrl = new URL(Theme.routes.cart_url, window.location.origin);
    sectionUrl.searchParams.set('section_id', this.#sectionId);

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(sectionUrl.toString(), { signal: this.#activeFetch.signal });
      if (!response.ok) throw new Error(`Failed to fetch cart share dialog: HTTP error ${response.status}`);

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      throw error;
    } finally {
      this.#activeFetch = null;
    }
  }

  async #prepareShareUrl(dialog) {
    const cart = await this.#fetchCartState();
    const shareUrl = this.#buildUrl(cart);
    const copyValue = dialog.querySelector('.copy-to-clipboard__value');
    const textarea = dialog.querySelector('[data-share-cart-textarea]');
    const socialLinks = Array.from(dialog.querySelectorAll('[data-share-platform]'));

    if (copyValue) copyValue.textContent = shareUrl;
    if (textarea instanceof HTMLInputElement) {
      textarea.value = shareUrl;
    }

    const encodedUrl = encodeURIComponent(shareUrl);

    socialLinks.forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      const originalHref = link.dataset.shareHref || link.getAttribute('href') || '';
      link.dataset.shareHref = originalHref;
      link.href = originalHref.replaceAll('__SHARE_URL__', encodedUrl);
    });
  }

  async #restoreSharedCart() {
    if (this.#restoreState === 'restored' || this.#restoreState === 'restoring') return;

    try {
      await this.#restoreIfNeeded();
    } catch {
      this.#restoreState = 'restored';
    }
  }

  async #fetchCartState() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Failed to load cart state');
    return response.json();
  }

  async #restoreIfNeeded() {
    if (this.#restorePromise) return this.#restorePromise;

    const url = new URL(window.location.href);

    if (!this.#hasSharedCartPayload(url)) {
      this.#restoreState = 'restored';
      return null;
    }

    const encodedPayload = this.#getRawPayload(url);
    if (!encodedPayload) {
      this.#restoreState = 'restored';
      return null;
    }

    if (this.#hasCartDrawer()) {
      await this.#prepareDrawerRestoreState();
    } else {
      this.#ensureRestoringOverlay();
      this.#setRestoringState(true);
    }

    this.#restoreState = 'restoring';
    this.#restorePromise = (async () => {
      try {
        const payload = this.#decodePayload(encodedPayload);
        const sectionsParam = this.#getSectionsToUpdate();

        await this.#clearCart();

        const cart = await this.#addItems(payload.items, sectionsParam);
        if (!cart) {
          this.#restoreState = 'restored';
          return null;
        }


        const updateCompleted = this.#waitForCartUpdated();

        document.dispatchEvent(new CartUpdateEvent(cart, this.#source, {
          source: this.#source,
          itemCount: this.#getItemCount(payload.items),
          sections: cart.sections || {},
        }));

        await updateCompleted;

        this.#removeShareState();
        this.#restoreState = 'restored';
        return payload;
      } finally {
        this.#setRestoringState(false);
        this.#removeRestoringOverlay();
      }
    })();

    return this.#restorePromise;
  }

  #getSectionsToUpdate() {
    const sectionsToUpdate = new Set();

    document.querySelectorAll(this.#cartItemsSelector).forEach((element) => {
      if (element.dataset.sectionId) {
        sectionsToUpdate.add(element.dataset.sectionId.replace('shopify-section-', ''));
      }
    });

    const announcementBar = document.querySelector('announcement-bar');
    if (announcementBar?.querySelector('free-shipping-bar') && announcementBar.dataset.sectionId) {
      sectionsToUpdate.add(announcementBar.dataset.sectionId);
    }

    return Array.from(sectionsToUpdate).join(',');
  }

  #buildUrl(cart) {
    const items = this.#normalizeItems(
      cart?.items?.map((item) => ({ id: item.variant_id, quantity: item.quantity }))
    );

    if (items.length === 0) throw new Error('Cart is empty');

    const currentUrl = new URL(window.location.href);
    const shareUrl = this.#shouldUseCurrentPageUrl()
      ? new URL(window.location.href)
      : new URL(Theme.routes.cart_url, currentUrl.origin);

    shareUrl.search = '';

    this.#contextParams.forEach((param) => {
      const value = currentUrl.searchParams.get(param);
      if (value) shareUrl.searchParams.set(param, value);
    });

    shareUrl.hash = `${this.#hashPrefix}${items.map(({ id, quantity }) => `${id}:${quantity}`).join(',')}`;

    const finalShareUrl = shareUrl.toString();
    if (finalShareUrl.length > this.#urlLimit) throw new Error('Cart is too large to share');

    return finalShareUrl;
  }

  #getRawPayload(url) {
    if (!url.hash.startsWith(this.#hashPrefix)) return null;

    return url.hash.slice(this.#hashPrefix.length);
  }

  #decodePayload(encodedPayload) {
    const items = encodedPayload.split(',').map((item) => {
      const [id, quantity] = item.split(':');
      return { id: Number(id), quantity: Number(quantity) };
    });

    return { items: this.#normalizeItems(items) };
  }

  #normalizeItems(items) {
    if (!Array.isArray(items)) return [];

    return items.map((item) => {
      const id = Number(item?.id);
      const quantity = Number(item?.quantity ?? item?.q);

      if (!Number.isInteger(id) || id <= 0 || !Number.isInteger(quantity) || quantity <= 0) return null;

      return { id, quantity };
    }).filter(Boolean);
  }

  #getItemCount(items) {
    if (!Array.isArray(items)) return 0;

    return items.reduce((total, item) => total + Number(item?.quantity || 0), 0);
  }

  #removeShareState() {
    const url = new URL(window.location.href);

    if (url.hash.startsWith(this.#hashPrefix)) {
      url.hash = '';
    }

    history.replaceState({}, '', url.toString());
  }

  #setRestoringState(isRestoring) {
    this.#restoringContainer?.toggleAttribute(this.#restoringAttribute, isRestoring);
  }

  #waitForCartUpdated() {
    return new Promise((resolve) => {
      const handleCartUpdated = (event) => {
        if (event.detail?.sourceId !== this.#source) return;
        document.removeEventListener(ThemeEvents.cartUpdated, handleCartUpdated);
        resolve();
      };

      document.addEventListener(ThemeEvents.cartUpdated, handleCartUpdated);
    });
  }

  async #clearCart() {
    const response = await fetch(`${Theme.routes.cart_url}/clear.js`, fetchConfig('json'));
    if (!response.ok) throw new Error('Failed to clear cart');
  }

  async #addItems(items, sections = '') {
    let cart = null;

    for (const [index, item] of [...items].reverse().entries()) {
      const body = {
        items: [item],
      };

      if (index === items.length - 1 && sections) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', {
        body: JSON.stringify(body)
      }));

      if (!response.ok) return null;
      cart = await response.json();
    }

    return cart;
  }
}

if (!customElements.get('cart-share-component')) {
  customElements.define('cart-share-component', CartShareComponent);
}
